import React from 'react';

import BrellaWidget from './BrellaWidget';

// Brella widgets are authored as a plain-text marker, e.g. [brella:speakers:JoinCode].
// Plain text is the only thing that survives the CMS untouched: CKEditor's ACF strips
// `id`/`style` attributes and react-html-parser drops every <script> node, so the real
// Brella markup is synthesized by BrellaWidget rather than stored.
//
// This lives apart from utils/reactHtmlParserTransform so the events pages do not pull in
// that module's article/newsletter dependency graph.
const BRELLA_MARKER = /\[brella:([a-z-]+):([A-Za-z0-9_-]+)\]/g;

// String.split with a capturing regex yields [text, type, code, text, type, code, ..., text].
const splitOnBrellaMarkers = (data: string, keyPrefix: string) => {
  const parts = data.split(BRELLA_MARKER);
  if (parts.length === 1) return null;

  const out: (string | JSX.Element)[] = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) out.push(parts[i]);
    const widgetType = parts[i + 1];
    const joinCode = parts[i + 2];
    if (widgetType && joinCode) {
      out.push(
        <BrellaWidget key={`${keyPrefix}-${i}`} widgetType={widgetType} joinCode={joinCode} />
      );
    }
  }
  return out;
};

// Returning undefined lets react-html-parser fall through to its standard handling; it
// propagates this transform to children on its own. Anything but a marker is untouched.
const eventTransform = (node, index) => {
  if (node.type === 'text') {
    const rendered = splitOnBrellaMarkers(node.data ?? '', `brella-${index}`);
    if (rendered) {
      return <React.Fragment key={`brella-${index}`}>{rendered}</React.Fragment>;
    }
  }
  return undefined;
};

// Landing-page cards show a description excerpt; a widget must never mount there.
const stripBrellaMarkers = (text: string) => (text || '').replace(BRELLA_MARKER, '');

export { eventTransform, stripBrellaMarkers };
